import React, { useState, useEffect, useContext, FC } from "react";
import { useLocation } from "react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppStore } from "../app/store";
import routes from "../app/routes";
import { HomeIcon } from "./SvgIcons";
import { AnalyticsActionType } from "../app/contexts/Analytics/types";
import {
  getPageNameFromPathName,
  useShouldSendPageView,
} from "../app/contexts/Analytics/helpers";
import {
  ReferrerContext,
  initialReferrerContextState,
} from "../app/contexts/ReferrerContext/context";
import { ReferrerContextValues } from "../app/contexts/ReferrerContext/types";
import URI from "urijs";
import { parseSearchParams } from "../helpers";
import { routesWithQueryParamPages } from "../app/contexts/Analytics/constants";

export const routeReferrerContextMappings = {
  [routes.searchResults]: {
    goBackTextId: "app.terms.go_back_to_search_results",
  },
  [routes.comparePlans]: {
    goBackTextId: "app.terms.go_back_to_plan_compare",
  },
  [routes.yearOverYear]: {
    goBackTextId: "app.terms.go_back_to_year_over_year",
  },
  [routes.summary.landingPage]: {
    goBackTextId: "summary_page.title",
    icon: <HomeIcon />,
  },
  default: {
    goBackTextId: "app.terms.start_a_new_search",
  },
};

export const getPageName = ({
  pathname,
  queryParamPage,
}: {
  pathname: string;
  queryParamPage?: string | undefined;
}) => {
  const useQueryParamPage =
    routesWithQueryParamPages.includes(pathname) && queryParamPage;
  return `${getPageNameFromPathName(pathname)}${
    useQueryParamPage ? `/${queryParamPage.toLowerCase()}` : ""
  }`;
};

export const ReferrerDetector: FC = ({ children }) => {
  // Context
  const { state, dispatch } = useContext(AppStore.AppContext);

  // Hooks
  const location = useLocation();
  const { pathname, search } = location;
  const shouldSendPageView = useShouldSendPageView();

  // Flags
  const { mctCurrentPlanYear } = useFlags();

  // State
  const [previousLocation, setPreviousLocation] = useState("");
  const [previousPage, setPreviousPage] = useState("");
  const [contextValues, setContextValues] = useState(
    initialReferrerContextState
  );

  const updateContextValues = (values: Partial<ReferrerContextValues>) =>
    setContextValues({ ...contextValues, ...values });

  useEffect(() => {
    if (!state.tealiumLoaded) {
      return;
    }
    const routeShouldUseQueryParamPage =
      routesWithQueryParamPages.includes(pathname);
    if (routeShouldUseQueryParamPage && !search) {
      // Wait until query params are populated to determine what value should
      // be used to record a pageview, based on the value of "page" in query params
      return;
    }
    const uri = new URI(search);
    const { page: queryParamPage } = parseSearchParams(uri.search(true));
    const queryParamPageHasChanged = queryParamPage !== previousPage;
    const pageHasChanged = previousLocation !== pathname;
    let useQueryParamPage = false;

    if (queryParamPageHasChanged) {
      setPreviousPage(queryParamPage);
      if (queryParamPage && routeShouldUseQueryParamPage) {
        useQueryParamPage = true;
      }
    }

    if (pageHasChanged) {
      setPreviousLocation(pathname);
      if (!useQueryParamPage) {
        switch (previousLocation) {
          case routes.searchResults:
            updateContextValues(
              routeReferrerContextMappings[routes.searchResults]
            );
            break;
          case routes.comparePlans:
            updateContextValues(
              routeReferrerContextMappings[routes.comparePlans]
            );
            break;
          case routes.yearOverYear:
            updateContextValues(
              routeReferrerContextMappings[routes.yearOverYear]
            );
            break;
          case routes.summary.landingPage:
            updateContextValues(
              routeReferrerContextMappings[routes.summary.landingPage]
            );
            break;
          default:
            updateContextValues(routeReferrerContextMappings.default);
            break;
        }
      }
    }

    if (pageHasChanged || useQueryParamPage) {
      if (state.tealiumLoaded && shouldSendPageView) {
        dispatch({
          type: AnalyticsActionType.SEND_PAGE_VIEW,
          settings: {
            custom_page_name: queryParamPage,
            page_name: getPageName({ pathname, queryParamPage }),
            location,
            mctCurrentPlanYear,
          },
        });
      }
    }
  }, [pathname, search, state.tealiumLoaded]);

  return (
    <ReferrerContext.Provider
      value={{
        goBackTextId: contextValues.goBackTextId,
        icon: contextValues.icon,
      }}
    >
      {children}
    </ReferrerContext.Provider>
  );
};
